// Enhanced Authentication Hook for ERP System
import { useState, useEffect } from 'react';
import { User, Session } from '@supabase/supabase-js';
import authService, { UserProfile, UserRole } from '@/services/authService';

export interface AuthState {
  user: User | null;
  session: Session | null;
  profile: UserProfile | null;
  roles: UserRole[];
  loading: boolean;
  isAuthenticated: boolean;
}

export const useAuth = () => {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    session: null,
    profile: null,
    roles: [],
    loading: true,
    isAuthenticated: false,
  });

  useEffect(() => {
    const updateAuthState = () => {
      setAuthState({
        user: authService.getCurrentUser(),
        session: authService.getCurrentSession(),
        profile: authService.getUserProfile(),
        roles: authService.getUserRoles(),
        loading: false,
        isAuthenticated: authService.isAuthenticated(),
      });
    };

    // 移除定时刷新 - 由 Supabase autoRefreshToken 自动处理
    // 只在初始化时检查一次状态
    updateAuthState();
  }, []);

  const signIn = async (email: string, password: string) => {
    const result = await authService.signIn(email, password);
    setAuthState(prev => ({
      ...prev,
      user: result.user,
      profile: result.profile,
      isAuthenticated: !!result.user,
    }));
    return result;
  };

  const signOut = async () => {
    await authService.signOut();
    setAuthState({
      user: null,
      session: null,
      profile: null,
      roles: [],
      loading: false,
      isAuthenticated: false,
    });
  };

  const hasPermission = async (permission: string, companyId?: string) => {
    return authService.hasPermission(permission, companyId);
  };

  const hasRole = (role: string, companyId?: string) => {
    return authService.hasRole(role, companyId);
  };

  const isAdmin = () => {
    return authService.isAdmin();
  };

  const isManager = (companyId?: string) => {
    return authService.isManager(companyId);
  };

  const refreshProfile = async (signal?: AbortSignal) => {
    await authService.refreshProfile(signal);
    setAuthState(prev => ({
      ...prev,
      profile: authService.getUserProfile(),
      roles: authService.getUserRoles(),
    }));
  };

  return {
    ...authState,
    signIn,
    signOut,
    hasPermission,
    hasRole,
    isAdmin,
    isManager,
    refreshProfile,
  };
};

export default useAuth;
